import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

// CompetitorOverlapCell — table-cell-shaped button that on click opens a
// small popover showing per-exchange breakdown of this channel's
// competitor sponsorships in the chosen window. Click outside to close.
//
// Backed by GET /api/channels/:id/competitor-breakdown. By default only
// confirmed sponsorships are counted (matches the metric); the popover
// has a toggle to include pending too.
export function CompetitorOverlapCell({
  channelID,
  overlap,
  days = 90,
}: {
  channelID: number;
  overlap: number | null | undefined;
  days?: number;
}) {
  const [open, setOpen] = useState(false);
  const [includePending, setIncludePending] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Lazy-load: only fetch when the popover is opened.
  const q = useQuery({
    queryKey: ["competitor-breakdown", channelID, days, includePending],
    queryFn: () => endpoints.channelCompetitorBreakdown(channelID, days, includePending),
    enabled: open,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Suppress row's click handler (row navigates to detail drawer) — overlap
  // cell has its own interaction.
  const stopRow = (e: React.MouseEvent) => e.stopPropagation();

  if (overlap === null || overlap === undefined) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <div ref={wrapRef} className="relative inline-block" onClick={stopRow}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "rounded px-2 py-0.5 text-sm font-medium tabular-nums",
          overlap > 0
            ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
            : "bg-muted text-muted-foreground hover:bg-muted/80",
        )}
      >
        {overlap} {overlap > 0 && <span className="text-[10px]">▾</span>}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-80 rounded-md border border-border bg-background shadow-lg">
          <div className="border-b border-border px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">
                竞品推广 ({days}d)
              </span>
              <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <input
                  type="checkbox"
                  checked={includePending}
                  onChange={(e) => setIncludePending(e.target.checked)}
                  className="h-3 w-3"
                />
                含 pending
              </label>
            </div>
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              {includePending
                ? "包含未审核的推广（系统检测但你还没确认）"
                : "只算已确认的（与表格里数字口径一致）"}
            </p>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {q.isLoading && (
              <p className="p-3 text-xs text-muted-foreground">加载中…</p>
            )}
            {q.data && q.data.length === 0 && (
              <p className="p-3 text-xs text-muted-foreground">
                {includePending
                  ? "无任何检测到的竞品推广"
                  : "无已确认的竞品推广（试试勾选含 pending）"}
              </p>
            )}
            {q.data && q.data.length > 0 && (
              <ul className="divide-y divide-border">
                {q.data.map((row) => (
                  <li
                    key={row.exchange_id}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <div>
                      <div className="font-medium">{row.display_name}</div>
                      <div className="text-[10px] text-muted-foreground">
                        最近 {fmtDate(row.latest_video_at)}
                      </div>
                    </div>
                    <span className="rounded bg-muted px-2 py-0.5 text-xs tabular-nums">
                      {row.video_count} 条
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {q.data && q.data.length > 0 && (
            <div className="border-t border-border px-3 py-2 text-right">
              <Link
                to={`/channels/${channelID}#competitor-sponsorships`}
                className="text-xs text-primary hover:underline"
              >
                查看具体内容 →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
